import { useTenantContext } from 'src/services/user/TenantContext/helpers/hook'

type ModuleSlug =
  | 'APPROVAL_ENGINE'
  | 'MEDICAL_CONDITIONS'
  | 'QUALIFICATIONS_DATA'
  | 'MANAGERS_LOG'
  | 'DIARY_NOTES_PERFORMANCE_IMPROVEMENT'
  | 'PRIVATE_NOTES'
  | 'WORKFORCE_PLANNING'
  | 'CUSTOM_SELF_SERVICE_TILES'
  | 'CUSTOM_SELF_SERVICE_TILES_FILTERING'
  | 'CUSTOM_FIELDS'
  | 'SKILLS'
  | 'GOALS'
  | 'GOAL_TEMPLATES'
  | 'ORGANISATION_OBJECTIVES'
  | 'PERFORMANCE_REPORT'
  | 'COMPLIANCE_QUALIFICATIONS'
  | 'COMPLIANCE_PERFORMANCE_IMPROVEMENT_PLANS'
  | 'FORM_DESIGNS'
  | 'FORM_DESIGNS_CREATE_YOUR_OWN'
  | 'TEMPLATE_DESIGNS'
  | 'PULSE_ENGINE_SINGLE_RECURRING'
  | 'ANONYMOUS_FEEDBACK'
  | 'CONTRACT_OFFER_MAKING'
  | 'NATIVE_INTEGRATIONS'
  | 'CINTRA_CALENDAR'
  | 'CINTRA_HOLIDAY_BOOKINGS'
  | 'CINTRA_ABSENCE'
  | 'CINTRA_PAYSLIPS'
  | 'CINTRA_BANK_ACCOUNTS'
  | 'CINTRA_MANAGE_ABSENCE'
  | 'CINTRA_MANAGE_HOLIDAYS'
  | 'CINTRA_MANAGE_PAY_CLAIMS'
  | 'CINTRA_P45'
  | 'CINTRA_P60'
  | 'CINTRA_P11D'
  | 'CINTRA_DOCUMENTS'
  | 'CINTRA_PAY_CLAIMS'
  | 'GLOBAL_NOTIFICATIONS'
  | 'FIRST_LEVEL_SUPPORT'
  | 'MULTILINGUAL_SUPPORT'
  | 'COMPLIANCE_TASKS'
  | 'TRAININGS'
  | 'PEOPLE_CALENDAR'
  | 'COMPLIANCE_CONTRACTORS'
  | 'COMPLIANCE_EXTENDED_LEAVE'
  | 'ORGANISATION_CHART'
  | 'ANALYTICS_PAGE_BUSINESS_PERFORMANCE'
  | 'ANALYTICS_SETTINGS'
  | 'ANALYTICS_ACCESS_AND_PERMISSIONS'
  | 'WORKFLOW_ENGINE'
  | 'WORKFLOW_ENGINE_CHANGE_OF_ROLE'
  | 'WORKFLOW_ENGINE_EXTENDED_LEAVE'
  | 'WORKFLOW_ENGINE_ONBOARDING'
  | 'WORKFLOW_ENGINE_OFFBOARDING'
  | 'WORKFLOW_ENGINE_PERFORMANCE_IMPROVEMENT'
  | 'WORKFLOW_ENGINE_TRAINING'

const WORKFLOW_ENGINE_MODULE_SLUGS: ModuleSlug[] = [
  'WORKFLOW_ENGINE',
  'WORKFLOW_ENGINE_CHANGE_OF_ROLE',
  'WORKFLOW_ENGINE_EXTENDED_LEAVE',
  'WORKFLOW_ENGINE_ONBOARDING',
  'WORKFLOW_ENGINE_OFFBOARDING',
  'WORKFLOW_ENGINE_PERFORMANCE_IMPROVEMENT',
  'WORKFLOW_ENGINE_TRAINING',
]
/**
 * Given a slug or list of slugs, return a boolean of whether that module is enabled for the current user.
 *
 * When passing multiple slugs, this check will fail if _any_ of the slugs fail to pass the check and passAll flag is set to true
 *
 * @see \Lapis\Domain\Module\Services\ModuleCheckerService in lapis
 *
 * @param moduleSlug a slug or list of slugs as defined by ModuleSlug
 * @returns boolean
 */
const useModuleCheckWithSlug = (moduleSlug: ModuleSlug | ModuleSlug[], passAll = true): boolean => {
  if (!Array.isArray(moduleSlug)) {
    moduleSlug = [moduleSlug]
  }

  const enabledModules = useTenantContext()
    .modules.filter((module) => module.isEnabledForCurrentUser)
    .flatMap((module) => module.slug)

  if (passAll) {
    return moduleSlug.every((slug) => enabledModules.includes(slug))
  }

  return moduleSlug.some((slug) => enabledModules.includes(slug))
}

const useWorkflowEngineModuleEnabled = (): boolean => {
  // workflow is considered enabled if any of the workflow engine modules are enabled
  return useModuleCheckWithSlug(WORKFLOW_ENGINE_MODULE_SLUGS, false)
}

export { useModuleCheckWithSlug, useWorkflowEngineModuleEnabled }

export type { ModuleSlug }
